import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import AspectRatio from '../AspectRatio/AspectRatio';

const bem = new BEMHelper('Image');

const TRANSPARENT_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

const ProductsImageWithSkeleton = (props) => {
    const [imageState, setImageState] = useState({
        skeleton: true,
        error: false,
        imageStatus: '',
    });

    const imageRef = useRef(null);

    const removeSkeleton = () => {
        setImageState({ skeleton: false, error: false, imageStatus: 'loaded' });
    };

    const handleImageErrored = () => {
        setImageState({ ...imageState, error: true });
    };

    useEffect(() => {
        if (src && imageRef?.current && imageRef?.current?.src && imageRef?.current?.complete) {
            setImageState({
                ...imageState,
                imageStatus: 'loaded',
                skeleton: false,
            });
        }
    }, [imageRef]);

    const { skeleton, error, imageStatus } = imageState;
    const {
        alt,
        cover,
        lowQualitySrc,
        className,
        displayError,
        displaySrc,
        noLogo,
        withIntersectionObserver,
        withSkeleton,
        keepImageAspectRatio,
        hideOnMobile,
        imageSrcset,
        aspectRatio,
        src,
        plpSrcset = {
            desktopSrcset: null,
            mobileSrcset: null,
        },
    } = props;

    const errorModifier = noLogo ? null : error || displayError;
    const { imageContainer = className, image, aspectRatioClassNames } = className || {};
    const useSkeleton = withSkeleton ? skeleton : false;
    const status = useSkeleton ? 'loaded' : imageStatus;

    const contain = keepImageAspectRatio ? 'contain' : '';
    const containerOverride = contain && !skeleton;
    const containerSkeleton = contain && skeleton;

    const { desktopSrcset, mobileSrcset } = plpSrcset;

    const imageSrc = () => (
        <>
            {lowQualitySrc && <img src={lowQualitySrc} className={bem('lowQuality')} />}
            <picture>
                {hideOnMobile && <source media="(max-width: 1023px)" sizes="1px" srcSet={TRANSPARENT_GIF} />}
                <source media="(min-width: 1024px)" srcSet={desktopSrcset || imageSrcset} />
                <img
                    src={src}
                    srcSet={mobileSrcset || imageSrcset}
                    ref={imageRef}
                    className={bem('image', { contain, useSkeleton, error: errorModifier, cover, [imageStatus]: true }, image)}
                    onLoad={withSkeleton ? () => removeSkeleton() : undefined}
                    onError={() => handleImageErrored()}
                    alt={status === 'loaded' || error ? alt : ''}
                />
            </picture>
        </>
    );

    return (
        <AspectRatio aspectRatio={aspectRatio} className={aspectRatioClassNames}>
            <div
                className={bem(
                    'imageContainer',
                    { useSkeleton, containerOverride, containerSkeleton, error: errorModifier, [imageStatus || '']: true },
                    imageContainer,
                )}
            >
                {withIntersectionObserver ? displaySrc && imageSrc() : imageSrc()}
            </div>
        </AspectRatio>
    );
};

ProductsImageWithSkeleton.propTypes = {
    src: PropTypes.string,
    displaySrc: PropTypes.bool,
    computeAttributes: PropTypes.bool,
    alt: PropTypes.string,
    aspectRatio: PropTypes.string,
    imageSrcset: PropTypes.string,
    noscript: PropTypes.bool,
    cover: PropTypes.bool,
    displayError: PropTypes.bool,
    height: PropTypes.number,
    width: PropTypes.number,
    noLogo: PropTypes.bool,
    withIntersectionObserver: PropTypes.bool,
    withSkeleton: PropTypes.bool,
    srcset: {
        srcset: PropTypes.string,
        sizes: PropTypes.string,
        src: PropTypes.string,
    },
};

ProductsImageWithSkeleton.defaultProps = {
    displaySrc: true,
    cover: false,
    computeAttributes: true,
    displayError: false,
    withSkeleton: true,
    src: TRANSPARENT_GIF,
};

export default ProductsImageWithSkeleton;
